import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Select } from "../../components/ui";
import "../../components/inputs/select.scss";

/**
 * Who an EXPERT FEE was paid to — the merchant's own list, not Xero's.
 *
 * This is the EXPERT FEES row's replacement for PaymentRow's bank-account
 * dropdown. The distinction matters: a bank account is a real Xero ledger
 * account an invoice payment is posted against, whereas an expert is just a
 * label the merchant maintains (an authenticator, a watchmaker, a consultant).
 * Nothing here is seeded or hardcoded — per the SaaS rule, each store builds
 * its own list, and it is built from this dropdown because there is no separate
 * management screen.
 *
 * Typing a new name and confirming POSTs it, then selects the row that comes
 * back — so the value handed upward is always a real `expert_accounts.id`,
 * never the raw text. (The shared CustomSelect's `creatable` mode hands back
 * the typed string as the id and persists nothing, which would leave the fee
 * pointing at a name that does not exist.)
 */
export function ExpertAccountSelect({
  value,
  onChange,
  disabled = false,
  className,
  placeholder = "Select expert",
}) {
  const [options, setOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const fetchAccounts = useCallback(async () => {
    try {
      const { data } = await axios.get("/api/expert-accounts");
      setOptions(Array.isArray(data) ? data : []);
    } catch {
      // Not deployed on this env, or the caller lacks the grant — an empty
      // list still lets the rep create one, and the fee is optional anyway.
      setOptions([]);
    }
  }, []);

  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  const handleCreate = async (inputValue) => {
    const name = inputValue.trim();
    if (!name) return;

    setIsLoading(true);
    try {
      const { data } = await axios.post("/api/expert-accounts", { name });
      // The backend returns the EXISTING row when the name is already taken,
      // so re-adding a name selects it rather than erroring or duplicating.
      setOptions((prev) =>
        prev.some((o) => o.id === data.id) ? prev : [...prev, data]
      );
      onChange?.(data.id, data);
    } catch (e) {
      window.alert(
        e?.response?.data?.message || "Could not save that expert. Try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Ids as-is: unknown ids are dropped (not printed raw) while the list loads.
  const selected = options.some((o) => o.id === value) ? value : null;

  return (
    <Select
      className={["rc-select", className].filter(Boolean).join(" ")}
      fullWidth
      clearable
      label="Expert"
      placeholder={placeholder}
      disabled={disabled || isLoading}
      options={options.map((o) => ({ value: o.id, label: o.name }))}
      value={selected}
      // Hands back the whole record alongside the id: the Order Summary card
      // names the expert, and om holds only what this row gives it.
      onChange={(id) => onChange?.(id ?? null, options.find((o) => o.id === id) ?? null)}
      // Persists first, then selects the row the backend returns (handleCreate
      // calls onChange itself), so the value is always a real id.
      onCreate={handleCreate}
    />
  );
}

export default ExpertAccountSelect;
